import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type MouseEvent,
  type SyntheticEvent,
} from 'react';
import { formatCopy, namespaceCopyFor } from '@/i18n/ns';
import connectAccessCopy, { type ConnectAccessCopyKey } from '@/i18n/ns/connect-access';
import connectionActionCopy from '@/i18n/ns/connection-action';
import { formatCount } from '@/i18n/format';
import { useChatCopy, useChatInterfaceLocale } from '@/lib/chat-i18n';
import { useOptionalWorkspace } from '@/workspace/context';
import { Button } from '@/components/ui/button';
import { actionSurfaceCopy } from './action-surface-copy';
import { ActionExpiry, useActionExpiry } from './action-expiry';
import { publicValue, visibleFields } from './action-field-privacy';
import { useActionConfirmation } from './use-action-confirmation';
import type { ActionOrigin, ActionPointer, ActionView } from './action-confirmation';

/** The connected-tool calls whose cards group into one plan (DES3 connect-access §3, §7). */
export const CONNECTOR_ACTION_TOOLS = new Set(['call_connection_tool', 'call_custom_mcp_tool']);

type Copy = Readonly<Record<ConnectAccessCopyKey, string>>;
type RowState =
  | 'waiting'
  | 'running'
  | 'done'
  | 'skipped'
  | 'declined'
  | 'expired'
  | 'failed'
  | 'unknown';

/** What a row reports to its group: enough to count, label the buttons and decide. */
interface RowReport {
  id: string;
  status: ActionView['status'] | null;
  pending: boolean;
  decidable: boolean;
  ready: boolean;
  isRead: boolean;
  service: string | null;
  expiresAt: string | null;
  state: RowState | null;
}

interface RowHandle {
  approve: (event: SyntheticEvent<HTMLButtonElement>) => Promise<void>;
  reject: () => Promise<void>;
}

/**
 * The provider's own top-level arguments the person reads (see `visibleFields`).
 */
function rowFields(action: ActionView) {
  const inner = action.args.arguments;
  return visibleFields(
    inner && typeof inner === 'object' && !Array.isArray(inner)
      ? (inner as Record<string, unknown>)
      : {},
    action.decision.connection?.fields ?? {},
  );
}

const shown = (value: unknown) => {
  const visible = publicValue(value);
  return typeof visible === 'string' || typeof visible === 'number' || typeof visible === 'boolean'
    ? String(visible)
    : JSON.stringify(visible);
};

function rowState(
  action: ActionView | null,
  skipped: boolean,
  uncertain: boolean,
): RowState | null {
  if (!action) return null;
  if (uncertain) return 'unknown';
  switch (action.status) {
    case 'pending':
      return null;
    case 'running':
      return 'running';
    case 'executed':
      return 'done';
    case 'rejected':
    case 'superseded':
      return skipped ? 'skipped' : 'declined';
    case 'expired':
      return 'expired';
    case 'failed':
      return 'failed';
  }
}

const ROW_COPY: Record<RowState, keyof Copy> = {
  waiting: 'rowWaiting',
  running: 'rowRunning',
  done: 'rowDone',
  skipped: 'rowSkipped',
  declined: 'rowDeclined',
  expired: 'rowExpired',
  failed: 'rowFailed',
  unknown: 'rowUnknown',
};

/**
 * One planned connected call inside the group. It keeps its own action, card grant and
 * approval exactly as a single card would; the group only decides when to send them.
 */
function ConnectorApprovalRow({
  pointer,
  origin,
  fresh,
  copy,
  checked,
  skipped,
  onCheck,
  onReport,
  handles,
}: {
  pointer: ActionPointer;
  origin: ActionOrigin;
  fresh: boolean;
  copy: Copy;
  checked: boolean;
  skipped: boolean;
  onCheck: (id: string, checked: boolean) => void;
  onReport: (report: RowReport) => void;
  handles: Map<string, RowHandle>;
}) {
  const state = useActionConfirmation(pointer, 'compact', fresh, origin);
  const { action } = state;
  const id = pointer.action.id;
  const connection = action?.decision.connection;
  const pending = action?.status === 'pending' && !state.uncertain;
  const decidable = Boolean(pending && action?.canDecide);
  const current = rowState(action, skipped, state.uncertain);
  // The latest send functions, read by the group in the person's own click.
  useLayoutEffect(() => {
    handles.set(id, {
      approve: (event) => state.mutate('approve', undefined, event),
      reject: () => state.mutate('reject'),
    });
  });
  useEffect(() => () => void handles.delete(id), [handles, id]);
  useEffect(() => {
    onReport({
      id,
      status: action?.status ?? null,
      pending: Boolean(pending),
      decidable,
      ready: state.canApprove,
      isRead: connection?.isRead === true,
      service: connection?.name ?? null,
      expiresAt: action?.expiresAt ?? null,
      state: current,
    });
  }, [
    id,
    action?.status,
    action?.expiresAt,
    pending,
    decidable,
    state.canApprove,
    connection?.isRead,
    connection?.name,
    current,
    onReport,
  ]);
  if (state.loadError) return null;
  if (!action)
    return (
      <div
        role="listitem"
        className="pc-connector-row"
        aria-busy="true"
        ref={state.presentation.ref}
      />
    );
  const title = connection?.title || action.label;
  const fields = rowFields(action);
  const checkboxId = `${id}-include`;
  return (
    <div
      role="listitem"
      className="pc-connector-row"
      data-state={current ?? 'waiting'}
      ref={state.presentation.ref}
    >
      {decidable && (
        <input
          id={checkboxId}
          type="checkbox"
          checked={checked}
          disabled={state.busy}
          aria-label={formatCopy(copy.rowInclude, { action: title })}
          onChange={(event) => onCheck(id, event.target.checked)}
        />
      )}
      <div className="pc-connector-row-main">
        <p className="pc-connector-row-title">
          <span>{title}</span>
          <span className="pc-connector-tag" data-kind={connection?.isRead ? 'read' : 'change'}>
            {connection?.isRead ? copy.tagRead : copy.tagChange}
          </span>
        </p>
        {fields.length > 0 && (
          <dl className="pc-connector-fields">
            {fields.map(([key, value]) => {
              // The reviewed title, else the schema title the server sent; the bare key only in Details.
              const label = connection?.fields?.[key];
              return label ? (
                <div key={key}>
                  <dt>{label}</dt>
                  <dd>{shown(value)}</dd>
                </div>
              ) : null;
            })}
          </dl>
        )}
        <details className="pc-connector-details">
          <summary>{copy.rowDetails}</summary>
          <dl>
            {typeof action.args.tool === 'string' && (
              <div>
                <dt>
                  <code>tool</code>
                </dt>
                <dd>
                  <code>{action.args.tool}</code>
                </dd>
              </div>
            )}
            {fields.map(([key, value]) => (
              <div key={key}>
                <dt>
                  <code>{key}</code>
                </dt>
                <dd>{shown(value)}</dd>
              </div>
            ))}
          </dl>
        </details>
        {/* A refused approval says why beside its row, as the single card does. */}
        {pending && state.notice && (
          <p className="pc-action-confirmation-notice" role="alert">
            {state.notice}
          </p>
        )}
      </div>
      {current && (
        <span className="pc-connector-row-state" role="status">
          {copy[ROW_COPY[current]]}
        </span>
      )}
    </div>
  );
}

/**
 * Connected calls staged in one assistant message, decided together (DES3 connect-access §3/§7,
 * DES1's checkbox / Approve {n} / Don't allow / Expires {time}). Each row keeps its own existing
 * action, digest and card grant; Approve sends each checked row's own approval in the person's
 * trusted click and declines each unchecked one. Nothing is batched on the server or widened.
 */
export function ConnectorApprovalGroup(props: {
  items: { pointer: ActionPointer; origin: ActionOrigin }[];
  fresh: boolean;
  hasFollowingReply?: boolean;
}) {
  const workspace = useOptionalWorkspace();
  const chatCopy = useChatCopy();
  const surface = actionSurfaceCopy(useChatInterfaceLocale());
  // Shared readers have no authority over the owner's actions: the same inert notice as a single
  // card, from readable thread metadata only, and no action or presented request is sent.
  if (workspace?.readOnly)
    return (
      <section className="pc-action-status-line" role="status">
        {surface.waitingFor(workspace.threadMeta?.ownerName ?? chatCopy.account)}
      </section>
    );
  return <OwnedConnectorApprovalGroup {...props} />;
}

function OwnedConnectorApprovalGroup({
  items,
  fresh,
  hasFollowingReply = false,
}: {
  items: { pointer: ActionPointer; origin: ActionOrigin }[];
  fresh: boolean;
  hasFollowingReply?: boolean;
}) {
  const locale = useChatInterfaceLocale();
  const copy: Copy = namespaceCopyFor(connectAccessCopy, locale.split('-')[0]);
  const surface = actionSurfaceCopy(locale);
  const heading = useId();
  const handles = useRef(new Map<string, RowHandle>()).current;
  const [reports, setReports] = useState<Record<string, RowReport>>({});
  const [unchecked, setUnchecked] = useState<Set<string>>(() => new Set());
  const [skipped, setSkipped] = useState<Set<string>>(() => new Set());
  const [sending, setSending] = useState(false);
  const onReport = useCallback(
    (report: RowReport) => setReports((current) => ({ ...current, [report.id]: report })),
    [],
  );
  const onCheck = useCallback((id: string, checked: boolean) => {
    setUnchecked((current) => {
      const next = new Set(current);
      if (checked) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  const ids = items.map((item) => item.pointer.action.id);
  const rows = ids.map((id) => reports[id]).filter((row): row is RowReport => Boolean(row));
  const open = rows.filter((row) => row.decidable);
  const included = open.filter((row) => !unchecked.has(row.id));
  const changes = rows.filter((row) => !row.isRead).length;
  const services = [...new Set(rows.map((row) => row.service).filter(Boolean))] as string[];
  const service = services.length === 1 ? services[0] : null;
  const soonest = open
    .map((row) => row.expiresAt)
    .filter((value): value is string => Boolean(value))
    .sort()[0];
  const expiry = useActionExpiry(soonest ?? '');
  const ready = open.length > 0 && open.every((row) => row.ready) && !expiry.expired;

  async function decide(event: MouseEvent<HTMLButtonElement>, approve: boolean) {
    if (sending || !ready) return;
    setSending(true);
    const declined = new Set<string>();
    try {
      // In order, each through its own card: the checked ones approved in this trusted click.
      for (const row of open) {
        const handle = handles.get(row.id);
        if (!handle) continue;
        if (approve && !unchecked.has(row.id)) await handle.approve(event);
        else {
          if (approve) declined.add(row.id);
          await handle.reject();
        }
      }
    } finally {
      setSkipped((current) => new Set([...current, ...declined]));
      setSending(false);
    }
  }

  const decided =
    rows.length === ids.length && rows.length > 0 && rows.every((row) => !row.pending);
  const approved = rows.filter((row) => row.state === 'running' || row.state === 'done').length;
  const outcome = !decided
    ? null
    : approved > 0
      ? formatCopy(copy.outcomeApproved, {
          actions: formatCount(copy.actionsNoun, approved, locale),
        })
      : rows.every((row) => row.state === 'expired')
        ? formatCopy(copy.outcomeExpired, { service: service ?? '' })
        : rows.every((row) => row.state === 'declined' || row.state === 'skipped')
          ? formatCopy(copy.outcomeDeclined, { service: service ?? '' })
          : null;

  return (
    <>
      <section
        className="pc-connector-group"
        aria-labelledby={heading}
        data-slot="connector-approvals"
      >
        {rows.length > 0 && (
          <header className="pc-connector-group-head">
            {service && <p className="pc-connector-service">{service}</p>}
            <p id={heading}>{copy.cardWants}</p>
          </header>
        )}
        <div role="list" className="pc-connector-rows">
          {items.map(({ pointer, origin }) => (
            <ConnectorApprovalRow
              key={pointer.action.id}
              pointer={pointer}
              origin={origin}
              fresh={fresh}
              copy={copy}
              checked={!unchecked.has(pointer.action.id)}
              skipped={skipped.has(pointer.action.id)}
              onCheck={onCheck}
              onReport={onReport}
              handles={handles}
            />
          ))}
        </div>
        {open.length > 0 && (
          <footer className="pc-connector-group-foot">
            <p className="pc-connector-footnote">
              {changes > 0
                ? formatCopy(copy.cardChangesFooter, {
                    changes: formatCount(copy.changesNoun, changes, locale),
                    service: service ?? '',
                  })
                : formatCopy(copy.cardReadsFooter, { service: service ?? '' })}
            </p>
            <div className="pc-connector-actions">
              <Button
                type="button"
                variant="outline"
                className="pc-connector-decline"
                disabled={sending || !ready}
                onClick={(event) => void decide(event, false)}
              >
                {copy.dontAllow}
              </Button>
              <Button
                type="button"
                className="pc-connector-approve"
                disabled={sending || !ready || included.length === 0}
                aria-busy={sending || undefined}
                onClick={(event) => void decide(event, true)}
              >
                {formatCopy(copy.approveCount, { n: String(included.length) })}
              </Button>
            </div>
            {soonest && <ActionExpiry expiresAt={soonest} copy={surface} locale={locale} near />}
          </footer>
        )}
        {outcome && (
          <p className="pc-connector-outcome" role="status">
            {outcome}
          </p>
        )}
      </section>
      <EarlierConnectionReply rows={rows} total={ids.length} visible={hasFollowingReply} />
    </>
  );
}

const TERMINAL_ROWS = new Set<RowState>(['done', 'declined', 'skipped', 'expired', 'failed']);

/** The current authenticated card reports, never a tool pointer's provisional outcome. */
function EarlierConnectionReply({
  rows,
  total,
  visible,
}: {
  rows: RowReport[];
  total: number;
  visible: boolean;
}) {
  const copy = namespaceCopyFor(connectionActionCopy, useChatInterfaceLocale().split('-')[0]);
  if (
    !visible ||
    total === 0 ||
    rows.length !== total ||
    !rows.every((row) => row.state !== null && TERMINAL_ROWS.has(row.state))
  )
    return null;
  return (
    <p className="pc-connector-earlier-reply" data-slot="connection-earlier-reply">
      {total === 1 ? copy.earlierReplyNoteOne : copy.earlierReplyNoteOther}
    </p>
  );
}
